<script setup>
import { computed, nextTick, reactive, ref } from 'vue'
import { KeyIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { apiErrorMessage } from '../../api.js'
import {
  createPartnerApiClient, listPartnerApiClients, revokePartnerApiClient,
  rotatePartnerApiClientSecret, updatePartnerApiClient,
} from '../../partnerApiClients.js'
import { useI18n } from '../../i18n.js'

const props = defineProps({ stations: { type: Array, default: () => [] } })
const { t } = useI18n()
const clients = ref([])
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const editorOpen = ref(false)
const editingId = ref('')
const revealed = ref(null)
const secretClose = ref(null)
const form = reactive({ displayName: '', status: 'ACTIVE', scopes: ['pts2.status.read'], stationCodes: [], tokenTtlSeconds: 300, version: null })
const editing = computed(() => Boolean(editingId.value))

function resetEditor() {
  editingId.value = ''
  Object.assign(form, { displayName: '', status: 'ACTIVE', scopes: ['pts2.status.read'], stationCodes: [], tokenTtlSeconds: 300, version: null })
  editorOpen.value = false
}

function openCreate() {
  resetEditor()
  editorOpen.value = true
}

function openEdit(client) {
  editingId.value = client.clientId
  Object.assign(form, { displayName: client.displayName, status: client.status, scopes: [...client.scopes], stationCodes: [...client.stationCodes], tokenTtlSeconds: client.tokenTtlSeconds, version: client.version })
  editorOpen.value = true
}

function toggle(values, value) {
  const index = values.indexOf(value)
  if (index >= 0) values.splice(index, 1)
  else values.push(value)
}

async function load() {
  loading.value = true
  error.value = ''
  try { clients.value = await listPartnerApiClients() } catch (cause) { error.value = apiErrorMessage(t, cause) }
  finally { loading.value = false }
}

async function save() {
  saving.value = true
  error.value = ''
  let createdSecret = null
  try {
    if (editing.value) {
      const updated = await updatePartnerApiClient(editingId.value, form)
      clients.value = clients.value.map((client) => client.clientId === updated.clientId ? updated : client)
    } else {
      const created = await createPartnerApiClient(form)
      createdSecret = created
      clients.value = [...clients.value, { ...created, clientSecret: undefined }]
    }
    resetEditor()
    if (createdSecret) await reveal(createdSecret)
  } catch (cause) { error.value = apiErrorMessage(t, cause) }
  finally { saving.value = false }
}

async function rotate(client) {
  error.value = ''
  try {
    const updated = await rotatePartnerApiClientSecret(client.clientId, client.version)
    await reveal(updated)
    clients.value = clients.value.map((item) => item.clientId === updated.clientId ? { ...updated, clientSecret: undefined } : item)
  } catch (cause) { error.value = apiErrorMessage(t, cause) }
}

async function reveal(client) {
  revealed.value = { clientId: client.clientId, clientSecret: client.clientSecret }
  await nextTick()
  secretClose.value?.focus()
}

function closeSecret() { revealed.value = null }

async function revoke(client) {
  if (!window.confirm(t('partnerApiRevokeConfirm', { name: client.displayName }))) return
  error.value = ''
  try {
    await revokePartnerApiClient(client.clientId, client.version)
    await load()
  } catch (cause) { error.value = apiErrorMessage(t, cause) }
}

async function copySecret() {
  try { await navigator.clipboard.writeText(revealed.value.clientSecret) } catch { error.value = t('partnerApiCopyFailed') }
}

load()
</script>

<template>
  <section class="partner-client-panel">
    <header class="platform-page-heading">
      <div><p class="eyebrow">OAuth 2.0</p><h1>{{ t('partnerApiClients') }}</h1><p>{{ t('partnerApiClientsHint') }}</p></div>
      <button class="button platform-primary" type="button" @click="openCreate"><PlusIcon aria-hidden="true" />{{ t('partnerApiAddClient') }}</button>
    </header>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>

    <div v-if="revealed" class="partner-secret" role="dialog" aria-modal="true" :aria-label="t('partnerApiOneTimeSecret')" @keydown.esc.prevent="closeSecret">
      <button ref="secretClose" class="text-action partner-secret-close" type="button" :aria-label="t('close')" @click="closeSecret"><XMarkIcon aria-hidden="true" /></button>
      <KeyIcon aria-hidden="true" /><div><strong>{{ t('partnerApiOneTimeSecret') }}</strong><p>{{ t('partnerApiOneTimeSecretHint') }}</p>
        <dl><dt>Client ID</dt><dd><code>{{ revealed.clientId }}</code></dd><dt>Client secret</dt><dd><code>{{ revealed.clientSecret }}</code></dd></dl>
        <button class="button" type="button" @click="copySecret">{{ t('copy') }}</button>
      </div>
    </div>

    <form v-if="editorOpen" class="platform-form-card partner-client-form" @submit.prevent="save">
      <header><h2>{{ editing ? t('partnerApiEditClient') : t('partnerApiNewClient') }}</h2><button class="text-action" type="button" @click="resetEditor">{{ t('cancel') }}</button></header>
      <div class="platform-form-grid">
        <label class="wide">{{ t('partnerApiDisplayName') }}<input v-model.trim="form.displayName" minlength="2" maxlength="120" required /></label>
        <label>{{ t('status') }}<select v-model="form.status"><option value="ACTIVE">{{ t('active') }}</option><option value="SUSPENDED">{{ t('partnerApiSuspended') }}</option></select></label>
        <label>{{ t('partnerApiTokenTtl') }}<input v-model.number="form.tokenTtlSeconds" type="number" min="60" max="900" required /></label>
      </div>
      <fieldset><legend>{{ t('partnerApiScopes') }}</legend>
        <label><input type="checkbox" :checked="form.scopes.includes('pts2.status.read')" @change="toggle(form.scopes, 'pts2.status.read')" />pts2.status.read</label>
        <label><input type="checkbox" :checked="form.scopes.includes('pts2.history.read')" @change="toggle(form.scopes, 'pts2.history.read')" />pts2.history.read</label>
      </fieldset>
      <fieldset><legend>{{ t('partnerApiStationGrants') }}</legend>
        <label v-for="station in stations" :key="station.stableCode"><input type="checkbox" :checked="form.stationCodes.includes(station.stableCode)" @change="toggle(form.stationCodes, station.stableCode)" />{{ station.displayName }} <code>{{ station.stableCode }}</code></label>
      </fieldset>
      <footer><button class="button platform-primary" type="submit" :disabled="saving || !form.scopes.length || !form.stationCodes.length">{{ saving ? t('saving') : t('save') }}</button></footer>
    </form>

    <p v-if="loading" class="platform-loading">{{ t('loading') }}</p>
    <div v-else-if="clients.length" class="partner-client-grid">
      <article v-for="client in clients" :key="client.clientId" class="partner-client-card">
        <header><div><small>{{ client.clientId }}</small><h2>{{ client.displayName }}</h2></div><span class="status-pill" :class="client.status.toLowerCase()">{{ client.status }}</span></header>
        <dl><dt>{{ t('partnerApiScopes') }}</dt><dd>{{ client.scopes.join(', ') }}</dd><dt>{{ t('partnerApiStationGrants') }}</dt><dd>{{ client.stationCodes.join(', ') }}</dd><dt>{{ t('partnerApiTokenTtl') }}</dt><dd>{{ client.tokenTtlSeconds }} s</dd></dl>
        <footer v-if="client.status !== 'REVOKED'"><button class="button" type="button" @click="openEdit(client)"><PencilSquareIcon aria-hidden="true" />{{ t('edit') }}</button><button class="button" type="button" @click="rotate(client)"><KeyIcon aria-hidden="true" />{{ t('partnerApiRotateSecret') }}</button><button class="text-action danger" type="button" @click="revoke(client)">{{ t('revoke') }}</button></footer>
      </article>
    </div>
    <div v-else class="catalog-empty-state"><KeyIcon class="catalog-empty-icon" aria-hidden="true" /><strong>{{ t('partnerApiNoClients') }}</strong><p>{{ t('partnerApiNoClientsHint') }}</p></div>
  </section>
</template>
